import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppShell } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { SearchBar } from '../components/SearchBar';
import {
  Badge,
  DataTable,
  PaginationFooter,
  SectionHeader,
  Skeleton,
  type DataTableColumn,
} from '../components/ui';
import { useActivityLogs } from '../features/activity/hooks/useActivityLogs';
import type { ActivityLogItem } from '../types/admin';
import styles from './ActivityPage.module.scss';

const rowsPerPageOptions = [10, 25, 50] as const;

export function ActivityPage() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const actionFilterOptions: { label: string; value: string | null }[] = [
    { label: t('activity.all'), value: null },
    { label: t('activity.patient'), value: 'patient_created' },
    { label: t('activity.treatment'), value: 'treatment_created' },
    { label: t('activity.stage'), value: 'stage_updated' },
    { label: t('activity.chat'), value: 'chat_message_sent' },
    { label: t('activity.assigned'), value: 'chat_assigned' },
  ];

  const search = searchParams.get('q') ?? '';
  const actionFilter = searchParams.get('action');
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';
  const page = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) - 1 || 0, 0);
  const rowsPerPage = rowsPerPageOptions.includes(
    Number.parseInt(searchParams.get('pageSize') ?? '10', 10) as 10 | 25 | 50,
  )
    ? (Number.parseInt(searchParams.get('pageSize') ?? '10', 10) as 10 | 25 | 50)
    : 10;

  const { data, error, isError, isLoading } = useActivityLogs({
    search: search || undefined,
    action: actionFilter ?? undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    page: page + 1,
    page_size: rowsPerPage,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  function updateSearchParams(
    next: Partial<{
      q: string;
      action: string | null;
      date_from: string;
      date_to: string;
      page: number;
      pageSize: number;
    }>,
    replace = false,
  ) {
    const updated = new URLSearchParams(searchParams);

    if (next.q !== undefined) {
      next.q ? updated.set('q', next.q) : updated.delete('q');
    }
    if (next.action !== undefined) {
      next.action ? updated.set('action', next.action) : updated.delete('action');
    }
    if (next.date_from !== undefined) {
      next.date_from ? updated.set('date_from', next.date_from) : updated.delete('date_from');
    }
    if (next.date_to !== undefined) {
      next.date_to ? updated.set('date_to', next.date_to) : updated.delete('date_to');
    }
    if (next.page !== undefined) {
      next.page > 1 ? updated.set('page', String(next.page)) : updated.delete('page');
    }
    if (next.pageSize !== undefined) {
      next.pageSize !== 10 ? updated.set('pageSize', String(next.pageSize)) : updated.delete('pageSize');
    }

    setSearchParams(updated, { replace });
  }

  function formatDateTime(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  const hasFilters = search || actionFilter || dateFrom || dateTo;

  const columns: DataTableColumn<ActivityLogItem>[] = [
    {
      key: 'time',
      header: t('activity.time'),
      render: (item) => <span className={styles.secondaryText}>{formatDateTime(item.created_at)}</span>,
    },
    {
      key: 'admin',
      header: t('activity.admin'),
      render: (item) => item.admin_name ?? t('common.system'),
    },
    {
      key: 'action',
      header: t('activity.action'),
      render: (item) => (
        <Badge variant="accent">
          {t(`activity.actionLabels.${item.action}`, { defaultValue: item.action })}
        </Badge>
      ),
    },
    {
      key: 'patient',
      header: t('activity.patientColumn'),
      className: styles.hideOnMobile,
      render: (item) =>
        item.patient_name ?? <span className={styles.secondaryText}>—</span>,
    },
    {
      key: 'summary',
      header: t('activity.summary'),
      className: styles.hideOnTablet,
      render: (item) => <span className={styles.summaryText}>{item.summary}</span>,
    },
  ];

  return (
    <AppShell>
      <div className={styles.page}>
        <SectionHeader title={t('activity.title')} subtitle={t('activity.entries', { count: total })} />

        <div className={styles.filters}>
          <SearchBar
            value={search}
            onChange={(value) => updateSearchParams({ q: value, page: 1 }, true)}
            placeholder={t('activity.search')}
          />
          <div className={styles.filterChips}>
            {actionFilterOptions.map((opt) => (
              <button
                type="button"
                key={opt.label}
                className={`${styles.chip} ${actionFilter === opt.value ? styles.chipActive : ''}`}
                onClick={() => updateSearchParams({ action: opt.value, page: 1 })}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.dateFilters}>
          <label className={styles.dateLabel}>
            <span>{t('activity.from')}</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              onChange={(e) => updateSearchParams({ date_from: e.target.value, page: 1 })}
            />
          </label>
          <label className={styles.dateLabel}>
            <span>{t('activity.to')}</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateTo}
              onChange={(e) => updateSearchParams({ date_to: e.target.value, page: 1 })}
            />
          </label>
          {hasFilters && (
            <button
              type="button"
              className={styles.clearFiltersBtn}
              onClick={() => updateSearchParams({ q: '', action: null, date_from: '', date_to: '', page: 1 })}
            >
              {t('activity.clearFilters')}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} width="100%" height={56} />
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            title={t('activity.loadErrorTitle')}
            description={error instanceof Error ? error.message : t('activity.loadErrorDescription')}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('activity.emptyTitle')}
            description={hasFilters ? t('activity.emptyFiltered') : t('activity.emptyDefault')}
          />
        ) : (
          <DataTable<ActivityLogItem>
            columns={columns}
            rows={items}
            rowKey={(item) => item.id}
            onRowClick={(item) => {
              if (item.patient_id) navigate(`/clients/${item.patient_id}`);
            }}
            footer={
              <PaginationFooter
                page={page + 1}
                pageSize={rowsPerPage}
                total={total}
                pageSizeOptions={[...rowsPerPageOptions]}
                onPageChange={(nextPage) => updateSearchParams({ page: nextPage })}
                onPageSizeChange={(size) => updateSearchParams({ pageSize: size, page: 1 })}
                labels={{
                  rowsPerPage: t('activity.rowsPerPage'),
                  rangeOf: (from, to, totalCount) =>
                    t('activity.ofTotal', { from, to, total: totalCount }),
                  previous: t('activity.previousPage'),
                  next: t('activity.nextPage'),
                }}
              />
            }
          />
        )}
      </div>
    </AppShell>
  );
}
